import React from 'react';
import ContactForm from '../components/ContactForm';
import useScrollAnimation from '../hooks/useScrollAnimation';
import siteContent from '../content';

const { hero, info, form, team: teamSection } = siteContent.pages.contact.sections;
const { team, address: globalAddress } = siteContent.global;

const Contact: React.FC = () => {
  const infoSection = useScrollAnimation({ threshold: 0 });
  const formSection = useScrollAnimation({ threshold: 0 });
  const mapSection = useScrollAnimation({ threshold: 0 });

  return (
    <>
      <title>Contact Us | Kline Real Estate Works</title>
      <meta
        name="description"
        content="Get in touch with Kline Real Estate Works. Located at 10 N. 5th Ave., Royersford, PA 19468. Contact us for all your real estate needs."
      />
      <meta property="og:title" content="Contact Us | Kline Real Estate Works" />
      <meta property="og:description" content="Get in touch with Kline Real Estate Works. Located at 10 N. 5th Ave., Royersford, PA 19468. Contact us for all your real estate needs." />
      <meta property="og:image" content="https://keithklinegroup.com/og-image.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content="https://keithklinegroup.com/og-image.jpg" />

      {/* Hero Section */}
      <section
        className="hero hero-with-image"
        data-cms-page="contact"
        data-cms-section="hero"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 43, 92, 0.75), rgba(0, 43, 92, 0.85)), url('${hero.image}')`
        }}
        data-cms-field="image"
        data-cms-type="image"
      >
        <div className="hero-content">
          <p className="hero-subtitle" data-cms-field="subtitle">{hero.subtitle}</p>
          <h1 data-cms-field="heading">{hero.heading}</h1>
          <p data-cms-field="description">{hero.description}</p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="section" data-cms-page="contact" data-cms-section="info">
        <div className="container">
          <div
            ref={infoSection.ref}
            className={`scroll-fade-up ${infoSection.isVisible ? 'visible' : ''}`}
          >
            <div className="grid grid-3">
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${globalAddress.street} ${globalAddress.city} ${globalAddress.state} ${globalAddress.zip}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="card contact-card"
              >
                <span className="material-icons contact-card-icon" data-cms-field="address.icon" data-cms-field-type="icon">
                  {info.address.icon}
                </span>
                <h3 data-cms-field="address.title">{info.address.title}</h3>
                <p data-cms-page="global" data-cms-section="address">
                  <span data-cms-field="street">{globalAddress.street}</span><br />
                  <span data-cms-field="city">{globalAddress.city}</span>, <span data-cms-field="state">{globalAddress.state}</span> <span data-cms-field="zip">{globalAddress.zip}</span>
                </p>
              </a>
              <a
                href="tel:+16109485739"
                className="card contact-card"
              >
                <span className="material-icons contact-card-icon" data-cms-field="phone.icon" data-cms-field-type="icon">
                  {info.phone.icon}
                </span>
                <h3 data-cms-field="phone.title">{info.phone.title}</h3>
                <p data-cms-field="phone.number">{info.phone.number}</p>
              </a>
              <a
                href={`mailto:${info.email.address}`}
                className="card contact-card"
              >
                <span className="material-icons contact-card-icon" data-cms-field="email.icon" data-cms-field-type="icon">
                  {info.email.icon}
                </span>
                <h3 data-cms-field="email.title">{info.email.title}</h3>
                <p data-cms-field="email.address">{info.email.address}</p>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Map Section */}
      <section className="section section-bg">
        <div className="container">
          <div className="two-column" style={{ alignItems: 'stretch' }}>
            <div
              ref={formSection.ref}
              className={`scroll-fade-left ${formSection.isVisible ? 'visible' : ''}`}
            >
              <ContactForm
                title={form.title}
                description={form.description}
              />
            </div>
            <div
              ref={mapSection.ref}
              className={`scroll-fade-right ${mapSection.isVisible ? 'visible' : ''}`}
              style={{ height: '100%' }}
            >
              <div style={{ height: '100%', borderRadius: '12px', overflow: 'hidden' }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3052.0!2d-75.5395!3d40.1855!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDDCsDExJzA4LjAiTiA3NcKwMzInMjIuMiJX!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0, display: 'block' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Kline Real Estate Works Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Contact Section */}
      <section className="section" data-cms-page="contact" data-cms-section="team">
        <div className="container">
          <div className="section-header">
            <h2 data-cms-field="heading">{teamSection.heading}</h2>
            <p data-cms-field="subheading">{teamSection.subheading}</p>
          </div>
          <div className="team-grid" data-cms-list="team" data-cms-page="global" data-cms-section="team">
            {team.map((member, index) => (
              <div
                key={member.id}
                className="card"
                style={{ padding: '1.5rem', textAlign: 'center' }}
                data-cms-index={index}
              >
                <img
                  src={member.image}
                  alt={member.name}
                  data-cms-field="team"
                  data-cms-index={index}
                  data-cms-item-field="image"
                  data-cms-type="image"
                  style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    objectPosition: 'top',
                    margin: '0 auto 1rem'
                  }}
                />
                <h4 style={{ marginBottom: '0.25rem' }} data-cms-field="team" data-cms-index={index} data-cms-item-field="name">{member.name}</h4>
                <p style={{ color: 'var(--color-gold-dark)', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem' }} data-cms-field="team" data-cms-index={index} data-cms-item-field="role">
                  {member.role}
                </p>
                <a
                  href={`mailto:${member.email}`}
                  style={{ fontSize: '0.85rem', color: 'var(--color-gray-600)' }}
                  data-cms-field="team"
                  data-cms-index={index}
                  data-cms-item-field="email"
                >
                  {member.email}
                </a>
                {member.phone && (
                  <p style={{ marginTop: '0.25rem' }}>
                    <a
                      href={`tel:${member.phone.replace(/\D/g, '')}`}
                      style={{ fontSize: '0.85rem', color: 'var(--color-gray-600)' }}
                      data-cms-field="team"
                      data-cms-index={index}
                      data-cms-item-field="phone"
                    >
                      {member.phone}
                    </a>
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
